import React, { useState, useMemo, useEffect } from "react";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { themeColors } from "../../utils/theme";

export interface NavItemChild {
  text: string;
  path?: string;
  icon?: React.ReactNode;
  children?: NavItemChild[];
}

interface SidebarItemProps {
  icon: React.ReactNode;
  text: string;
  path?: string;
  expanded: boolean;
  onClick?: () => void;
  children?: NavItemChild[];
  level?: number;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  text,
  path = "#",
  expanded,
  onClick,
  children,
  level = 0,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChildrenExpanded, setIsChildrenExpanded] = useState(false);

  const isActive = useMemo(() => {
    if (!path || path === "#") return false;
    return (
      location.pathname === path ||
      (path !== "/dashboard" && location.pathname.startsWith(path))
    );
  }, [path, location.pathname]);

  const isChildActive = useMemo(() => {
    if (!children) return false;
    const checkPathMatch = (items: NavItemChild[]): boolean => {
      return items.some((child) => {
        const pathMatch =
          child.path &&
          (location.pathname === child.path ||
            location.pathname.startsWith(child.path));
        const childrenMatch =
          child.children && checkPathMatch(child.children);
        return pathMatch || childrenMatch;
      });
    };
    return checkPathMatch(children);
  }, [children, location.pathname]);

  // Auto-expand menu when navigating to a child route
  useEffect(() => {
    if (isChildActive && !isChildrenExpanded) {
      setIsChildrenExpanded(true);
    }
  }, [isChildActive, isChildrenExpanded]);

  const handleClick = () => {
    if (children && !onClick) {
      setIsChildrenExpanded(!isChildrenExpanded);
    } else if (onClick) {
      onClick();
    } else if (path && path !== "#") {
      navigate(path);
    }
  };

  const handleChildClick = (childPath?: string) => {
    if (childPath) {
      navigate(childPath);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Arrow Down: Expand menu if collapsed or move to next item
    if (e.key === "ArrowDown" && children && !isChildrenExpanded) {
      e.preventDefault();
      setIsChildrenExpanded(true);
    }
    // Arrow Up: Collapse menu if expanded
    if (e.key === "ArrowUp" && children && isChildrenExpanded) {
      e.preventDefault();
      setIsChildrenExpanded(false);
    }
  };

  const paddingLeft = level > 0 ? 2.5 + (level * 2) : 2.5;

  const item = (
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemButton
        sx={{
          minHeight: 48,
          justifyContent: expanded ? "initial" : "center",
          px: 2.5,
          pl: paddingLeft,
          backgroundColor:
            isActive || isChildActive
              ? `${themeColors.primary}20`
              : "transparent",
          borderLeft:
            isActive || isChildActive
              ? `3px solid ${themeColors.primary}`
              : "3px solid transparent",
          transition: "background-color 0.2s ease, border-left-color 0.2s ease",
          "&:hover": {
            backgroundColor: `${themeColors.primary}10`,
          },
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-expanded={children ? isChildrenExpanded : undefined}
        aria-controls={children ? `nav-${text}-menu` : undefined}
        aria-label={`${text}${
          children ? ` (${isChildrenExpanded ? "expanded" : "collapsed"})` : ""
        }`}
        aria-current={isActive ? "page" : undefined}
        role={children ? "button" : "menuitem"}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: expanded ? 3 : "auto",
            justifyContent: "center",
            color: isActive || isChildActive ? themeColors.primary : "inherit",
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={text}
          sx={{
            opacity: expanded ? 1 : 0,
            color: isActive || isChildActive ? themeColors.primary : "inherit",
            "& .MuiTypography-root": {
              fontWeight: isActive || isChildActive ? 500 : 400,
            },
          }}
        />
        {children && expanded && (
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: "flex-end",
              color: isChildActive ? themeColors.primary : "inherit",
            }}
          >
            {isChildrenExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </ListItemIcon>
        )}
      </ListItemButton>
    </ListItem>
  );

  return (
    <>
      {expanded ? (
        item
      ) : (
        <Tooltip title={text} placement="right">
          {item}
        </Tooltip>
      )}
      {children && expanded && (
        <Collapse
          in={isChildrenExpanded}
          timeout="auto"
          unmountOnExit
          role="region"
          aria-label={`${text} submenu`}
          id={`nav-${text}-menu`}
        >
          {children.map((child, index) => {
            const childHasChildren = child.children && child.children.length > 0;
            
            // If child has children, render as a nested SidebarItem
            if (childHasChildren) {
              return (
                <SidebarItem
                  key={child.path || `nested-${index}`}
                  icon={child.icon || <span style={{ fontSize: "0.5rem" }}>•</span>}
                  text={child.text}
                  path={child.path}
                  expanded={expanded}
                  children={child.children}
                  level={level + 1}
                />
              );
            }
            
            // Otherwise render as a simple list item
            const childIsActive = child.path && location.pathname === child.path;
            return (
              <ListItem
                key={child.path || `nested-${index}`}
                disablePadding
                sx={{
                  display: "block",
                  my: 0.5,
                }}
              >
                <ListItemButton
                  sx={{
                    minHeight: 40,
                    justifyContent: "initial",
                    pl: 2.5 + ((level + 2) * 2),
                    pr: 2.5,
                    backgroundColor: childIsActive
                      ? `${themeColors.primary}20`
                      : "transparent",
                    borderLeft: childIsActive
                      ? `3px solid ${themeColors.primary}`
                      : "3px solid transparent",
                    fontSize: "0.8rem",
                    transition:
                      "background-color 0.2s ease, border-left-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: `rgba(65, 200, 196, 0.1)`,
                    },
                    "&:active": {
                      backgroundColor: `rgba(65, 200, 196, 0.15)`,
                    },
                  }}
                  onClick={() => handleChildClick(child.path)}
                  role="menuitem"
                  aria-current={childIsActive ? "page" : undefined}
                  aria-label={child.text}
                  tabIndex={childIsActive ? 0 : -1}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 20,
                      width: 20,
                      height: 20,
                      mr: 2,
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center",
                      color: childIsActive ? themeColors.primary : "inherit",
                      fontSize: "1rem",
                    }}
                  >
                    {child.icon || "•"}
                  </ListItemIcon>
                  <ListItemText
                    primary={child.text}
                    sx={{
                      color: childIsActive ? themeColors.primary : "inherit",
                      "& .MuiTypography-root": {
                        fontWeight: childIsActive ? 500 : 400,
                        fontSize: "0.875rem",
                        letterSpacing: "0.5px",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </Collapse>
      )}
    </>
  );
};

export default SidebarItem;
